import { getApi } from '../apiClient';
import {
  ILesson,
  IGameplay,
  ILessonGetResponse,
  ILessonGameplaysFinishPutRequest,
  ILessonGameplaysPostResponse,
  ILessonPhrasesGetResponse,
  ILessonSessionGetResponse,
  ILessonSessionPostResponse,
  ILessonSessionResultsGetsResponse,
  ILessonFeedback
} from './types';

// An author's preview of a lesson (see `lessonPreview`) reads the preview endpoints, which serve unpublished
// content of the author's teams.
export function lessonPath(lessonId: ILesson['id'], preview = false): string {
  return preview ? `/previews/lessons/${lessonId}` : `/lessons/${lessonId}`;
}

// lesson data
export async function fetchLesson(
  lessonId: ILesson['id'],
  preview = false
): Promise<ILessonGetResponse> {
  const { data } = await getApi().get<ILessonGetResponse>(lessonPath(lessonId, preview));

  return data;
}

export async function followTeam(teamId: number): Promise<void> {
  await getApi().patch(`/teams/${teamId}/follow`);
}

export async function unfollowTeam(teamId: number): Promise<void> {
  await getApi().patch(`/teams/${teamId}/unfollow`);
}

// lesson gameplays
export async function fetchCreateGameplay(
  lessonId: ILesson['id']
): Promise<ILessonGameplaysPostResponse> {
  const { data } = await getApi().post<ILessonGameplaysPostResponse>(
    `/lessons/${lessonId}/gameplays`
  );

  return data;
}

export async function fetchPlayStudentWord(
  wordId: number,
  solved: boolean
): Promise<void> {
  await getApi().post(`/words/${wordId}/student_words/play`, {
    solved
  });
}

export async function fetchFinishGameplay(
  lessonId: ILesson['id'],
  gameplayId: IGameplay['id'],
  gameplay: ILessonGameplaysFinishPutRequest['gameplay']
): Promise<void> {
  await getApi().put(`/lessons/${lessonId}/gameplays/${gameplayId}/finish`, {
    gameplay
  });
}

// phrases
export async function fetchLessonPhrases(
  lessonId: ILesson['id'],
  preview = false
): Promise<ILessonPhrasesGetResponse> {
  const { data } = await getApi().get<ILessonPhrasesGetResponse>(
    `${lessonPath(lessonId, preview)}/phrases`
  );

  return data;
}

// lessonSession
export async function fetchLessonSession(
  lessonId: ILesson['id'],
  preview = false
): Promise<ILessonSessionGetResponse> {
  const { data } = await getApi().get<ILessonSessionGetResponse>(
    `${lessonPath(lessonId, preview)}/session`
  );

  return data;
}

export async function startLessonSession(
  lessonId: ILesson['id']
): Promise<ILessonSessionPostResponse> {
  const { data } = await getApi().post<ILessonSessionPostResponse>(
    `/lessons/${lessonId}/session`
  );

  return data;
}

// A fresh preview session of the lesson, at `taskId` or at its first task.
export async function startPreviewSession(
  lessonId: ILesson['id'],
  taskId?: number
): Promise<ILessonSessionPostResponse> {
  const { data } = await getApi().post<ILessonSessionPostResponse>(
    `${lessonPath(lessonId, true)}/session`,
    null,
    { params: taskId ? { task_id: taskId } : {} }
  );

  return data;
}

// sessionResults
export async function fetchLessonSessionResults(
  sessionId: number
): Promise<ILessonSessionResultsGetsResponse> {
  const { data } = await getApi().get<ILessonSessionResultsGetsResponse>(
    `/lesson_sessions/${sessionId}/results`
  );

  return data;
}

// lessonRating
export async function fetchLessonRating(lessonId: number): Promise<ILessonFeedback> {
  const { data } = await getApi().get(`/lessons/${lessonId}/rating`);

  return data;
}

export async function submitLessonFeedback(
  lessonId: number,
  body: ILessonFeedback
): Promise<ILessonFeedback> {
  const { data } = await getApi().post(`/lessons/${lessonId}/rating`, body);

  return data;
}

export async function updateLessonFeedback(
  lessonId: number,
  body: ILessonFeedback
): Promise<ILessonFeedback> {
  const { data } = await getApi().patch(`/lessons/${lessonId}/rating`, body);

  return data;
}

export async function createCompletedLessonObjective(
  lessonId: number,
  objectiveId: number
): Promise<any> {
  const { data } = await getApi().post(`/lessons/${lessonId}/completed_objectives`, {
    id: objectiveId
  });

  return data;
}

export async function deleteCompletedLessonObjective(
  lessonId: number,
  objectiveId: number
): Promise<any> {
  const { data } = await getApi().delete(
    `/lessons/${lessonId}/completed_objectives/${objectiveId}`
  );

  return data;
}
